import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Megaphone, TrendingUp, MousePointerClick, Users, ArrowRight } from "lucide-react";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { PaymentOverdueBanner } from "@/components/payment-overdue-banner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});

type RecentCampaign = {
  id: string;
  ads_name: string;
  status: string;
  created_at: string;
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const y = Math.floor(mo / 12);
  return `${y} year${y === 1 ? "" : "s"} ago`;
}

function DashboardPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("there");
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const [recent, setRecent] = useState<RecentCampaign[] | null>(null);
  const { isBlocked } = usePaymentStatus();

  useEffect(() => {
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData.user;
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarded, full_name, email")
        .eq("id", user.id)
        .maybeSingle();

      if (profile && profile.onboarded === false) {
        navigate({ to: "/onboarding", replace: true });
      }

      const displayName =
        profile?.full_name?.trim()?.split(" ")[0] ||
        (profile?.email ?? user.email ?? "").split("@")[0] ||
        "there";
      setName(displayName);

      const [{ count }, { data: recentRows }] = await Promise.all([
        supabase
          .from("campaigns")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("status", "Active"),
        supabase
          .from("campaigns")
          .select("id, ads_name, status, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(3),
      ]);

      setActiveCount(count ?? 0);
      setRecent(recentRows ?? []);
    })();
  }, [navigate]);

  const pendingKpis = [
    { label: "Impressions (30d)", icon: TrendingUp },
    { label: "Clicks (30d)", icon: MousePointerClick },
    { label: "Leads (30d)", icon: Users },
  ];

  return (
    <AppShell title="Dashboard">
      <div className="mx-auto max-w-7xl space-y-8">
        {isBlocked && <PaymentOverdueBanner />}
        <section
          className="surface-panel relative overflow-hidden p-8"
          style={{ backgroundImage: "var(--gradient-glow)" }}
        >
          <div className="relative">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Welcome back, {name}
            </p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold text-gradient">
              Your NexAEO command center.
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Track campaign performance and submit new Meta Ads requests directly to your dedicated
              agency team — all in one place.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild={!isBlocked} variant="hero" size="lg" disabled={isBlocked} aria-disabled={isBlocked}>
                {isBlocked ? (
                  <span>Launch a Campaign <ArrowRight /></span>
                ) : (
                  <Link to="/campaigns/new">
                    Launch a Campaign <ArrowRight />
                  </Link>
                )}
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/campaigns">View Campaigns</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="surface-panel p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Active Campaigns</span>
              <Megaphone className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-3 text-2xl font-semibold">
              {activeCount === null ? "—" : activeCount}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Live now</div>
          </div>

          {pendingKpis.map((k) => (
            <div key={k.label} className="surface-panel p-5 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-3 text-2xl font-semibold blur-sm select-none opacity-60">
                000.0K
              </div>
              <div className="mt-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  Syncing soon
                </span>
              </div>
            </div>
          ))}
        </section>

        <section className="surface-panel p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">Recent Activity</h3>
            <span className="text-xs text-muted-foreground">Latest campaigns</span>
          </div>
          {recent === null ? (
            <p className="text-sm text-muted-foreground py-3">Loading…</p>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3">
              No activity yet. Launch your first campaign to see updates here.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {recent.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-3 gap-4">
                  <span className="truncate">
                    Campaign '{c.ads_name}' is currently {c.status}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {timeAgo(c.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
